/** Evaluaciones de fisioterapia — plantillas configurables por clínica (persistidas en notas EVAL_FISIO). */

import type { Nota } from './api';

export const EVAL_FISIO_TIPO = 'EVAL_FISIO';

export type CampoTipo = 'number' | 'text' | 'textarea' | 'select';

export type CampoEvaluacion = {
  id: string;
  etiqueta: string;
  tipo: CampoTipo;
  unidad?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  opciones?: string[];
  requerido?: boolean;
};

export type PlantillaEvaluacion = {
  id: string;
  nombre: string;
  objetivo: string;
  campos: CampoEvaluacion[];
};

export type EvaluacionFisio = {
  plantillaId: string;
  plantillaNombre: string;
  autor: string;
  campos: Record<string, string | number | null>;
  observaciones?: string;
  registradaEn?: string;
};

const LS_PREFIX = 'fisio-eval-plantillas:';

export const PLANTILLAS_EVALUACION_DEFAULT: PlantillaEvaluacion[] = [
  {
    id: 'dolor',
    nombre: 'Evaluación de dolor',
    objetivo: 'Intensidad, patrón y localización del dolor.',
    campos: [
      { id: 'eva_reposo', etiqueta: 'EVA en reposo', tipo: 'number', min: 0, max: 10, unidad: '/10', requerido: true },
      { id: 'eva_esfuerzo', etiqueta: 'EVA al esfuerzo', tipo: 'number', min: 0, max: 10, unidad: '/10' },
      { id: 'localizacion', etiqueta: 'Localización', tipo: 'text', placeholder: 'Ej. región lumbar paravertebral D' },
      { id: 'irradiacion', etiqueta: 'Irradiación', tipo: 'text', placeholder: 'Ej. cara posterior muslo' },
      { id: 'tipo_dolor', etiqueta: 'Características', tipo: 'select', opciones: ['Punzante', 'Sordo', 'Quemante', 'Eléctrico', 'Opresivo'] },
    ],
  },
  {
    id: 'edema',
    nombre: 'Evaluación de edema',
    objetivo: 'Volumen, grado y comparación bilateral.',
    campos: [
      { id: 'grado_edema', etiqueta: 'Grado de edema', tipo: 'select', opciones: ['0 Sin edema', '1+ Leve', '2+ Moderado', '3+ Severo', '4+ Muy severo'], requerido: true },
      { id: 'localizacion', etiqueta: 'Región', tipo: 'text', placeholder: 'Ej. tobillo derecho' },
      { id: 'perimetro', etiqueta: 'Perímetro', tipo: 'number', min: 0, unidad: 'cm' },
      { id: 'perimetro_contralateral', etiqueta: 'Perímetro lado contrario', tipo: 'number', min: 0, unidad: 'cm' },
      { id: 'fovea', etiqueta: 'Fóvea', tipo: 'select', opciones: ['Ausente', 'Presente', 'No evaluada'] },
    ],
  },
  {
    id: 'sensibilidad',
    nombre: 'Evaluación de sensibilidad',
    objetivo: 'Integridad de modalidades sensitivas.',
    campos: [
      { id: 'modalidad', etiqueta: 'Modalidad evaluada', tipo: 'select', opciones: ['Táctil ligera', 'Térmica', 'Dolorosa', 'Vibratoria', 'Proprioceptiva'], requerido: true },
      { id: 'area', etiqueta: 'Área / dermatoma', tipo: 'text', placeholder: 'Ej. L5 derecho' },
      { id: 'resultado', etiqueta: 'Resultado', tipo: 'select', opciones: ['Normal', 'Hipoestesia', 'Hipestesia', 'Disestesia', 'Anestesia'] },
      { id: 'comparacion', etiqueta: 'Comparación contralateral', tipo: 'select', opciones: ['Simétrico', 'Asimétrico', 'No aplica'] },
    ],
  },
  {
    id: 'fuerza',
    nombre: 'Fuerza muscular (MMT)',
    objetivo: 'Grado de fuerza por grupo muscular.',
    campos: [
      { id: 'grupo_muscular', etiqueta: 'Grupo muscular', tipo: 'text', placeholder: 'Ej. cuádriceps', requerido: true },
      { id: 'lado', etiqueta: 'Lado', tipo: 'select', opciones: ['Derecho', 'Izquierdo', 'Bilateral'] },
      { id: 'mmt', etiqueta: 'MMT', tipo: 'number', min: 0, max: 5, requerido: true },
      { id: 'dolor_asociado', etiqueta: 'Dolor al esfuerzo', tipo: 'select', opciones: ['No', 'Leve', 'Moderado', 'Severo'] },
    ],
  },
  {
    id: 'reflejos',
    nombre: 'Evaluación de reflejos',
    objetivo: 'Respuesta refleja osteotendinosa.',
    campos: [
      { id: 'reflejo', etiqueta: 'Reflejo', tipo: 'select', opciones: ['Bíceps', 'Tríceps', 'Rotuliano', 'Aquileo', 'Mediano', 'Otro'], requerido: true },
      { id: 'lado', etiqueta: 'Lado', tipo: 'select', opciones: ['Derecho', 'Izquierdo', 'Bilateral'] },
      { id: 'grado', etiqueta: 'Grado', tipo: 'select', opciones: ['0 Ausente', '1+ Hipoactivo', '2+ Normal', '3+ Hiperactivo', '4+ Clonus'] },
      { id: 'comparacion', etiqueta: 'Comparación contralateral', tipo: 'text', placeholder: 'Ej. simétrico' },
    ],
  },
  {
    id: 'rom',
    nombre: 'Amplitud articular (ROM)',
    objetivo: 'Rango activo y pasivo por articulación.',
    campos: [
      { id: 'articulacion', etiqueta: 'Articulación', tipo: 'text', placeholder: 'Ej. hombro', requerido: true },
      { id: 'movimiento', etiqueta: 'Movimiento', tipo: 'text', placeholder: 'Ej. abducción', requerido: true },
      { id: 'rom_activo', etiqueta: 'ROM activo', tipo: 'number', min: 0, max: 360, unidad: '°' },
      { id: 'rom_pasivo', etiqueta: 'ROM pasivo', tipo: 'number', min: 0, max: 360, unidad: '°' },
      { id: 'dolor_fin', etiqueta: 'Dolor al final del rango', tipo: 'select', opciones: ['No', 'Leve', 'Moderado', 'Severo'] },
    ],
  },
  {
    id: 'inicial',
    nombre: 'Evaluación inicial integral',
    objetivo: 'Tamiz funcional al inicio del plan de tratamiento.',
    campos: [
      { id: 'eva', etiqueta: 'EVA dolor', tipo: 'number', min: 0, max: 10, unidad: '/10', requerido: true },
      { id: 'rom_clave', etiqueta: 'ROM clave (°)', tipo: 'text', placeholder: 'Ej. flexión lumbar 40°' },
      { id: 'mmt_clave', etiqueta: 'MMT clave', tipo: 'text', placeholder: 'Ej. supraespinoso 3/5' },
      { id: 'funcionalidad', etiqueta: 'Limitación funcional', tipo: 'textarea', placeholder: 'Marcha, AVD, deporte…' },
      { id: 'objetivos', etiqueta: 'Objetivos del plan', tipo: 'textarea', placeholder: 'Metas SMART de la rehabilitación' },
    ],
  },
];

export function plantillasStorageKey(tenantId: string): string {
  return `${LS_PREFIX}${tenantId}`;
}

export function cargarPlantillas(tenantId: string): PlantillaEvaluacion[] {
  try {
    const raw = localStorage.getItem(plantillasStorageKey(tenantId));
    if (!raw) return structuredClone(PLANTILLAS_EVALUACION_DEFAULT);
    const parsed = JSON.parse(raw) as PlantillaEvaluacion[];
    return Array.isArray(parsed) && parsed.length ? parsed : structuredClone(PLANTILLAS_EVALUACION_DEFAULT);
  } catch {
    return structuredClone(PLANTILLAS_EVALUACION_DEFAULT);
  }
}

export function guardarPlantillas(tenantId: string, plantillas: PlantillaEvaluacion[]): void {
  localStorage.setItem(plantillasStorageKey(tenantId), JSON.stringify(plantillas));
}

export function restaurarPlantillasDefault(tenantId: string): PlantillaEvaluacion[] {
  localStorage.removeItem(plantillasStorageKey(tenantId));
  return structuredClone(PLANTILLAS_EVALUACION_DEFAULT);
}

export function plantillaPorId(plantillas: PlantillaEvaluacion[], id: string): PlantillaEvaluacion | undefined {
  return plantillas.find(p => p.id === id);
}

export function valoresVacios(plantilla: PlantillaEvaluacion): Record<string, string | number | null> {
  const v: Record<string, string | number | null> = {};
  for (const c of plantilla.campos) v[c.id] = c.tipo === 'number' ? null : '';
  return v;
}

export function serializeEvaluacion(ev: EvaluacionFisio): string {
  return JSON.stringify({
    plantillaId: ev.plantillaId,
    plantillaNombre: ev.plantillaNombre,
    autor: ev.autor,
    campos: ev.campos,
    observaciones: ev.observaciones?.trim() ?? '',
  });
}

export function parseEvaluacionNota(n: Nota): EvaluacionFisio | null {
  if (n.tipo !== EVAL_FISIO_TIPO) return null;
  try {
    const o = JSON.parse(n.contenido) as EvaluacionFisio;
    if (!o?.plantillaId || !o.campos) return null;
    return {
      plantillaId: o.plantillaId,
      plantillaNombre: o.plantillaNombre ?? o.plantillaId,
      autor: o.autor ?? n.autor,
      campos: o.campos,
      observaciones: o.observaciones ?? '',
      registradaEn: n.registradaEn,
    };
  } catch {
    return null;
  }
}

export function evaluacionesDesdeNotas(notas: Nota[]): EvaluacionFisio[] {
  return notas
    .filter(n => n.tipo === EVAL_FISIO_TIPO)
    .map(parseEvaluacionNota)
    .filter((e): e is EvaluacionFisio => e != null)
    .sort((a, b) => (b.registradaEn ?? '').localeCompare(a.registradaEn ?? ''));
}

export function resumenEvaluacion(ev: EvaluacionFisio, plantilla?: PlantillaEvaluacion): string {
  const partes: string[] = [];
  const campos = plantilla?.campos ?? [];
  for (const c of campos.slice(0, 4)) {
    const v = ev.campos[c.id];
    if (v === null || v === '' || v === undefined) continue;
    partes.push(`${c.etiqueta}: ${v}${c.unidad ? ` ${c.unidad}` : ''}`);
  }
  if (partes.length === 0) {
    const vals = Object.values(ev.campos).filter(v => v !== null && v !== '');
    if (vals.length) partes.push(String(vals[0]));
  }
  return partes.join(' · ') || '—';
}

export function validarEvaluacion(ev: EvaluacionFisio, plantilla: PlantillaEvaluacion): string | null {
  for (const c of plantilla.campos) {
    if (!c.requerido) continue;
    const v = ev.campos[c.id];
    if (v === null || v === '' || v === undefined) return `Complete «${c.etiqueta}».`;
  }
  return null;
}
